// All landing-page copy, sourced from the Rosa wiki and reframed to consumer value.
// Commercial positioning: sell families on the value (safe at home, one trusted
// specialist, fast, free). The "magic behind the scenes" (economics, sourcing,
// lead mechanics) stays OFF the page.

export type ProjectType = {
  id: string;
  label: string;
  short: string;
  blurb: string;
  icon: string; // key into Icons map
};

export const PROJECT_TYPES: ProjectType[] = [
  {
    id: 'walk_in_shower',
    label: 'Walk-in shower',
    short: 'Step-free, safe to enter',
    blurb: 'A curbless, step-free shower you can walk straight into, with no high tub wall to climb over.',
    icon: 'shower',
  },
  {
    id: 'tub_to_shower',
    label: 'Tub-to-shower conversion',
    short: 'Swap the tub you climb into',
    blurb: 'Replace a hard-to-enter bathtub with a level, easy-access shower. It is the single biggest bathroom safety change.',
    icon: 'convert',
  },
  {
    id: 'grab_bars',
    label: 'Grab bars',
    short: 'Something sturdy to hold',
    blurb: 'Properly anchored grab bars by the shower, tub and toilet, placed by someone who knows exactly where they belong.',
    icon: 'grabbar',
  },
  {
    id: 'stair_lift',
    label: 'Stair lift',
    short: 'Get up and down safely',
    blurb: 'A smooth, reliable stair lift so the stairs never become the reason to leave the home you love.',
    icon: 'stairlift',
  },
  {
    id: 'wheelchair_ramp',
    label: 'Wheelchair ramp',
    short: 'A safe way in and out',
    blurb: 'A well-built ramp to the front door: gentle, sturdy and made to fit the home, not bolted on as an afterthought.',
    icon: 'ramp',
  },
  {
    id: 'not_sure',
    label: "I'm not sure yet",
    short: 'Let the specialist advise',
    blurb: 'Not sure what the home needs? That is exactly what the free assessment is for. Your specialist will walk it with you.',
    icon: 'help',
  },
];

export type MobilityNeed = { id: string; label: string; note: string };

export const MOBILITY_NEEDS: MobilityNeed[] = [
  { id: 'independent', label: 'Walks independently', note: 'Steady, but a fall or near-miss has raised the worry.' },
  { id: 'cane_walker', label: 'Uses a cane or walker', note: 'Needs support and safe things to hold on to.' },
  { id: 'wheelchair', label: 'Uses a wheelchair', note: 'Needs step-free access and room to move.' },
  { id: 'recovering', label: 'Recovering from a fall or surgery', note: 'Home needs to be safe before discharge.' },
];

export type Timeline = { id: string; label: string; note: string; urgent?: boolean };

export const TIMELINES: Timeline[] = [
  { id: 'discharge', label: 'Before a hospital discharge', note: 'We understand the clock is ticking.', urgent: true },
  { id: 'asap', label: 'As soon as possible', note: 'This week if we can.', urgent: true },
  { id: 'few_weeks', label: 'In the next few weeks', note: 'Planning ahead, no rush.' },
  { id: 'exploring', label: 'Just exploring for now', note: 'Understanding the options first.' },
];

export type Budget = { id: string; label: string; range: string };

export const BUDGETS: Budget[] = [
  { id: 'under_2k', label: 'Under $2,000', range: 'Grab bars, small safety fixes' },
  { id: '2k_6k', label: '$2,000 – $6,000', range: 'Ramps, seats, fittings' },
  { id: '6k_15k', label: '$6,000 – $15,000', range: 'Walk-in shower, tub conversion' },
  { id: '15k_plus', label: '$15,000+', range: 'Larger accessibility remodels' },
  { id: 'unsure', label: 'Not sure, I want a quote', range: 'The assessment is free either way' },
];

// The four consumer-facing reasons families trust Rosa (from USPs, reframed).
export type Reason = { id: string; title: string; body: string; icon: string; stat?: string };

export const REASONS: Reason[] = [
  {
    id: 'exclusive',
    title: 'One specialist, never an auction',
    body: 'You are matched with a single trusted local specialist, not handed round to five companies who race to hound you with sales calls. One person, who is actually going to do the work.',
    icon: 'shield',
    stat: '1 : 1',
  },
  {
    id: 'caps',
    title: 'Every specialist is CAPS-certified',
    body: 'CAPS is the accredited training in home accessibility and aging-in-place safety. Every specialist Rosa matches you with holds it, and is background-checked. No guessing whether they really know this work.',
    icon: 'badge',
    stat: 'CAPS only',
  },
  {
    id: 'scoped',
    title: 'They call already knowing your situation',
    body: 'We ask the right questions up front: what happened, what the home needs, the timeline. Your specialist arrives prepared and scoped, so you are not starting from scratch on the phone.',
    icon: 'clipboard',
    stat: 'Prepared',
  },
  {
    id: 'free',
    title: 'Free, no pressure, no obligation',
    body: 'The in-home safety assessment is completely free, and you are never under any obligation to go ahead. You pay Rosa nothing, ever. It is help, not a funnel.',
    icon: 'heart',
    stat: '$0 to you',
  },
];

export type Step = { n: number; title: string; body: string; icon: string };

export const STEPS: Step[] = [
  {
    n: 1,
    title: 'Tell us what happened',
    body: 'A few quick questions about the home and what you need: location, the fix, the timeline. No account, no cost, a couple of minutes.',
    icon: 'chat',
  },
  {
    n: 2,
    title: 'We match one CAPS-certified specialist',
    body: 'Rosa finds the right vetted, background-checked local expert for your area and your project: a single specialist, never an auction.',
    icon: 'match',
  },
  {
    n: 3,
    title: 'They book your free assessment',
    body: 'Your specialist calls to schedule a free in-home safety assessment, usually within 48 hours, and walks you through every fix at eye level.',
    icon: 'calendar',
  },
];

// Interactive bathroom hotspots — educational, honest, non-alarmist.
export type Hotspot = { id: string; x: number; y: number; title: string; body: string };

export const BATHROOM_HOTSPOTS: Hotspot[] = [
  { id: 'shower', x: 30, y: 40, title: 'Curbless walk-in shower', body: 'No tub wall to climb over. You step straight in on the level. The single biggest change for bathroom safety.' },
  { id: 'grab', x: 62, y: 46, title: 'Grab bars where they belong', body: 'Anchored into the wall studs at the right height by the shower and toilet, sturdy enough to trust your full weight.' },
  { id: 'seat', x: 24, y: 66, title: 'A place to sit', body: 'A fold-down shower seat means no standing on a slippery floor to wash.' },
  { id: 'floor', x: 50, y: 86, title: 'Non-slip flooring', body: 'Textured, low-glare flooring that grips even when wet, with no more skating on polished tile.' },
  { id: 'taps', x: 78, y: 70, title: 'Lever taps & handheld shower', body: 'Easy-turn levers and a handheld head, so washing is comfortable seated or standing.' },
];

// Illustrative "who we're built for" voices, drawn from the persona research.
// Presented as the situations Rosa exists for — not as verified customer reviews.
export type Voice = { id: string; quote: string; name: string; role: string; tag: string; image: string };

export const VOICES: Voice[] = [
  {
    id: 'diane',
    quote:
      "I don't have time to vet five contractors on a good day — I definitely don't have time when I'm sitting in a hospital at 2 a.m. and Mom can't go home until the bathroom is safe.",
    name: 'The adult daughter, mid-crisis',
    role: 'Coordinating a parent’s care from two hours away',
    tag: 'After the fall',
    image: '/images/hero-care.webp',
  },
  {
    id: 'raymond',
    quote:
      "I built a career on knowing who the right expert was for a problem. I was embarrassed I couldn't figure out who that was for my own bathroom.",
    name: 'The independent homeowner',
    role: 'Staying in the home he loves, on his own terms',
    tag: 'Quietly planning',
    image: '/images/senior-independent.webp',
  },
  {
    id: 'karen',
    quote:
      "I can get someone stable and out of the bed, but if the bathroom that put them there hasn't changed, I'm just buying them a few weeks before I see them again.",
    name: 'The discharge coordinator',
    role: 'Sending families home to a safe place',
    tag: 'On the front line',
    image: '/images/contractor-explaining.webp',
  },
];

export type Faq = { q: string; a: string };

export const FAQS: Faq[] = [
  {
    q: 'What exactly does Rosa do?',
    a: 'Rosa helps families after a fall or a hospital stay find the right person to make the home safe. You tell us a little about the home and what you need, and we match you with one CAPS-certified local specialist who calls to arrange a free in-home safety assessment and quote.',
  },
  {
    q: 'Will I ever pay Rosa?',
    a: 'No. Families pay Rosa nothing, at any point. The in-home safety assessment is free and there is no obligation to go ahead. If you do decide to have work done, you arrange that directly with your specialist.',
  },
  {
    q: 'What does "one specialist, never an auction" mean?',
    a: 'Your request goes to a single CAPS-certified specialist for your area, not blasted out to five companies who all call you at once. You get one focused, prepared call from someone who is actually going to help, instead of a week of sales calls.',
  },
  {
    q: 'What is CAPS certification, and why does it matter?',
    a: 'CAPS stands for Certified Aging-in-Place Specialist, accredited training in accessibility and home safety. It is the difference between a general remodeler and someone who genuinely understands fall prevention and how to make a home work for you as you age. Every specialist Rosa matches you with holds it.',
  },
  {
    q: 'How quickly will someone get in touch?',
    a: 'Rosa is built for urgent moments, so we move fast. In most cases a specialist reaches out to schedule your assessment within about 48 hours, and we understand when the clock is a hospital discharge date.',
  },
  {
    q: 'Is the in-home safety assessment really free?',
    a: 'Yes, genuinely free, with no obligation. Your specialist will walk the home with you, point out the risks, and talk through the fixes in plain language. If you decide not to go ahead, you owe nothing.',
  },
  {
    q: 'Might my parent’s Medicare Advantage plan help cover the work?',
    a: 'Possibly. Since 2019, some Medicare Advantage plans can cover home-safety modifications under a benefit called SSBCI. Coverage varies by plan, so your CAPS-certified specialist can help you understand what yours may include.',
  },
  {
    q: 'How is my information used?',
    a: 'We use the details you share (your location, what the home needs, timeline and budget) only to match you with the right local specialist. We do not sell your information to a crowd of contractors; it goes to one.',
  },
];

// Metros used to make the coverage widget feel concrete (illustrative markets).
export const METROS: string[] = [
  'Columbus', 'Phoenix', 'Savannah', 'Atlanta', 'Dallas', 'Chicago', 'Charlotte',
  'Tampa', 'Denver', 'Portland', 'Nashville', 'Cincinnati', 'San Antonio', 'Sacramento',
];
